"use client";

import { defaultLocale, normalizeLocale } from "@/i18n/locale";
import { reportResourceToPayload } from "@/lib/mirofish-report-resource";
import type {
  MiroFishGraphPayload,
  MiroFishInteractionMessage,
  MiroFishProjectRecord,
  MiroFishProjectSummary,
  MiroFishReportResource,
  MiroFishSimulationPayload,
} from "@/types/mirofish";

export class MiroFishApiError extends Error {
  readonly status: number;
  readonly errorCode?: string;

  constructor(
    message: string,
    details: {
      status: number;
      errorCode?: string;
    },
  ) {
    super(message);
    this.name = "MiroFishApiError";
    this.status = details.status;
    this.errorCode = details.errorCode;
  }
}

const NON_TRANSIENT_API_ERROR_CODES = new Set([
  "KEY_POOL_BACKEND_UNREACHABLE",
  "ZEP_ACCOUNT_UNAVAILABLE",
  "REPORT_GENERATION_STALE",
]);

export const isTransientMiroFishApiError = (error: unknown) => {
  if (!(error instanceof Error)) {
    return false;
  }

  if (error instanceof MiroFishApiError) {
    const errorCode = error.errorCode?.trim().toUpperCase();
    if (
      error.status === 404 ||
      (errorCode && NON_TRANSIENT_API_ERROR_CODES.has(errorCode))
    ) {
      return false;
    }
  }

  const message = error.message.toLowerCase();
  if (
    message.includes("failed to fetch") ||
    message.includes("networkerror") ||
    message.includes("load failed") ||
    message.includes("timeout") ||
    message.includes("temporarily unavailable") ||
    message.includes("took too long to respond") ||
    message.includes("backend request failed")
  ) {
    return true;
  }

  return (
    error instanceof MiroFishApiError &&
    [500, 502, 503, 504, 524].includes(error.status)
  );
};

const throwApiError = (
  message: string,
  response: Response,
  errorCode?: string,
) => {
  throw new MiroFishApiError(message, {
    status: response.status,
    errorCode,
  });
};

const throwIfNotOk = async (response: Response) => {
  if (response.ok) {
    return response;
  }

  const body = await response.text().catch(() => "");
  const contentType = response.headers.get("content-type") || "";
  const trimmedBody = body.trim();
  const isHtmlError =
    contentType.includes("text/html") ||
    /^<!doctype html/i.test(trimmedBody) ||
    /cloudflare|gateway time-out|a timeout occurred/i.test(trimmedBody);
  const payload = (() => {
    if (!body.trim()) {
      return {} as { error?: string; error_code?: string; errorCode?: string };
    }
    if (isHtmlError) {
      return {
        error:
          "The simulation service took too long to respond. Please check History in a moment or try again.",
      };
    }
    try {
      return JSON.parse(body) as {
        error?: string;
        error_code?: string;
        errorCode?: string;
      };
    } catch {
      return {
        error: body.trim().slice(0, 500),
      };
    }
  })();
  const rawError = payload.error || "Request failed";
  const errorCode = payload.error_code || payload.errorCode;
  const normalized = rawError.toLowerCase();

  if ([502, 503, 504, 524].includes(response.status) && isHtmlError) {
    throwApiError(
      "The simulation service took too long to respond. Please check History in a moment or try again.",
      response,
      errorCode,
    );
  }

  if (
    errorCode === "MIROFISH_UPLOAD_SERVICE_UNAVAILABLE" ||
    normalized.includes("document upload service is temporarily unavailable")
  ) {
    throw new MiroFishUploadServiceUnavailableError(rawError);
  }

  if (
    errorCode === "graph_queue_unavailable" ||
    errorCode === "ontology_queue_unavailable" ||
    errorCode === "simulation_prepare_queue_unavailable" ||
    errorCode === "simulation_queue_unavailable" ||
    errorCode === "report_queue_unavailable"
  ) {
    throwApiError(
      "Background task service is temporarily unavailable. Please try again in a moment.",
      response,
      errorCode,
    );
  }

  if (errorCode === "key_pool_backend_unreachable") {
    throwApiError(
      "Zep account service is unavailable. Check local key-pool backend routing and retry.",
      response,
      errorCode,
    );
  }

  if (errorCode === "zep_account_unavailable") {
    throwApiError(
      "No unbound Zep account is available in the key pool. Add or release a Zep account and retry.",
      response,
      errorCode,
    );
  }

  if (errorCode === "report_generation_stale") {
    throwApiError(
      "The previous report run was interrupted. Start a new report run from Step 04.",
      response,
      errorCode,
    );
  }

  if (
    response.status === 429 ||
    normalized.includes("insufficient_quota") ||
    normalized.includes("exceeded your current quota")
  ) {
    throwApiError(
      "OpenAI API quota exceeded. Please check your OpenAI billing/usage and try again.",
      response,
      errorCode,
    );
  }

  throwApiError(rawError, response, errorCode);
};

class MiroFishUploadServiceUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "MiroFishUploadServiceUnavailableError";
  }
}

type MiroFishProjectCreateRetry = {
  retry: number;
  maxRetries: number;
  nextDelayMs: number;
};

type MiroFishProjectCreateOptions = {
  maxRetries?: number;
  onRetry?: (state: MiroFishProjectCreateRetry) => void;
  onProgress?: (state: { progress: number; label: string }) => void;
};

type MiroFishUploadInitResponse = {
  uploads: Array<{
    uploadId: string;
    fileId: string;
    objectKey: string;
    uploadUrl: string;
    headers?: Record<string, string>;
    expiresAt: string;
  }>;
};

const PROJECT_CREATE_RETRY_DELAYS_MS = [600, 1200, 2400];

const sleep = (ms: number) =>
  new Promise((resolve) => window.setTimeout(resolve, ms));

const isNetworkFetchError = (error: unknown) => {
  if (!(error instanceof Error)) {
    return false;
  }

  const message = error.message.toLowerCase();
  return (
    error instanceof TypeError ||
    message.includes("failed to fetch") ||
    message.includes("networkerror") ||
    message.includes("load failed")
  );
};

const cloneFormData = (formData: FormData) => {
  const next = new FormData();
  for (const [key, value] of formData.entries()) {
    if (value instanceof File) {
      next.append(key, value, value.name);
    } else {
      next.append(key, value);
    }
  }
  return next;
};

const getFileSha256 = async (file: File) => {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    await file.arrayBuffer(),
  );
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
};

const createSecureUUID = () => {
  const webCrypto = globalThis.crypto;
  if (typeof webCrypto?.randomUUID === "function") {
    return webCrypto.randomUUID();
  }

  if (typeof webCrypto?.getRandomValues === "function") {
    const bytes = new Uint8Array(16);
    webCrypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes)
      .map((byte) => byte.toString(16).padStart(2, "0"))
      .join("");
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`;
  }

  return `${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`;
};

export const createMiroFishClientSubmissionId = () =>
  `submission_${Date.now().toString(36)}_${createSecureUUID()}`;

const isLocalDevelopmentHost = () => {
  if (typeof window === "undefined") {
    return false;
  }

  return ["localhost", "127.0.0.1", "::1"].includes(window.location.hostname);
};

const getFileContentType = (file: File) => {
  if (file.type) {
    return file.type;
  }
  const lower = file.name.toLowerCase();
  if (lower.endsWith(".pdf")) {
    return "application/pdf";
  }
  if (lower.endsWith(".md") || lower.endsWith(".markdown")) {
    return "text/markdown";
  }
  if (lower.endsWith(".txt")) {
    return "text/plain";
  }
  return "application/octet-stream";
};

export const uploadMiroFishSourceDocuments = async ({
  files,
  clientSubmissionId,
  onProgress,
}: {
  files: File[];
  clientSubmissionId: string;
  onProgress?: MiroFishProjectCreateOptions["onProgress"];
}) => {
  onProgress?.({ progress: 12, label: "Preparing source documents..." });
  const fileInputs = await Promise.all(
    files.map(async (file, index) => ({
      file,
      fileId: `file_${index + 1}_${createSecureUUID()}`,
      name: file.name,
      size: file.size,
      contentType: getFileContentType(file),
      sha256: await getFileSha256(file),
    })),
  );

  onProgress?.({ progress: 22, label: "Opening secure upload session..." });
  const initResponse = await fetch("/api/mirofish/uploads/init", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      clientSubmissionId,
      files: fileInputs.map(({ file: _file, ...input }) => input),
    }),
  });
  await throwIfNotOk(initResponse);
  const initPayload = (await initResponse.json()) as MiroFishUploadInitResponse;

  const uploadIds: string[] = [];
  for (const [index, upload] of initPayload.uploads.entries()) {
    const input = fileInputs.find((item) => item.fileId === upload.fileId);
    if (!input) {
      throw new Error("Upload session did not match the selected files.");
    }

    onProgress?.({
      progress: 30 + Math.round((index / Math.max(1, files.length)) * 35),
      label: `Uploading ${input.file.name}...`,
    });
    const uploadResponse = await fetch(upload.uploadUrl, {
      method: "PUT",
      headers: upload.headers || {
        "Content-Type": input.contentType,
      },
      body: input.file,
    });
    if (!uploadResponse.ok) {
      throw new Error("Document upload failed. Please try again.");
    }
    uploadIds.push(upload.uploadId);
  }

  onProgress?.({ progress: 70, label: "Verifying uploaded documents..." });
  const completeResponse = await fetch("/api/mirofish/uploads/complete", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      clientSubmissionId,
      uploadIds,
    }),
  });
  await throwIfNotOk(completeResponse);

  return uploadIds;
};

export const createMiroFishProjectFromFiles = async ({
  prompt,
  files,
  options = {},
}: {
  prompt: string;
  files: File[];
  options?: MiroFishProjectCreateOptions;
}) => {
  const clientSubmissionId = createMiroFishClientSubmissionId();
  let uploadIds: string[];
  try {
    uploadIds = await uploadMiroFishSourceDocuments({
      files,
      clientSubmissionId,
      onProgress: options.onProgress,
    });
  } catch (error) {
    // Direct-to-storage uploads are blocked on localhost (bucket CORS only
    // allows production origins), so route the bytes through our own server
    // instead. Production keeps the exact previous behavior.
    if (
      isLocalDevelopmentHost() &&
      (error instanceof MiroFishUploadServiceUnavailableError ||
        isNetworkFetchError(error))
    ) {
      options.onProgress?.({
        progress: 24,
        label: "Using local document upload...",
      });
      const formData = new FormData();
      formData.set("prompt", prompt);
      files.forEach((file) => formData.append("files", file, file.name));
      return createMiroFishProject(formData, options);
    }
    if (isNetworkFetchError(error)) {
      throw new Error(
        "Document upload could not reach storage. Retry the upload or import a public file link.",
      );
    }
    throw error;
  }

  options.onProgress?.({ progress: 78, label: "Creating project..." });
  const maxRetries = Math.max(
    0,
    options.maxRetries ?? PROJECT_CREATE_RETRY_DELAYS_MS.length,
  );
  let lastNetworkError: unknown;

  for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
    try {
      const response = await fetch("/api/mirofish/projects", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prompt,
          clientSubmissionId,
          uploadIds,
        }),
      });

      await throwIfNotOk(response);
      return (await response.json()) as { project: MiroFishProjectRecord };
    } catch (error) {
      if (!isNetworkFetchError(error) || attempt >= maxRetries) {
        if (lastNetworkError && isNetworkFetchError(error)) {
          throw new Error(
            `Network interrupted while creating the project. Retried ${maxRetries} times. Please try again.`,
          );
        }
        throw error;
      }

      lastNetworkError = error;
      const nextDelayMs =
        PROJECT_CREATE_RETRY_DELAYS_MS[
          Math.min(attempt, PROJECT_CREATE_RETRY_DELAYS_MS.length - 1)
        ];
      options.onRetry?.({
        retry: attempt + 1,
        maxRetries,
        nextDelayMs,
      });
      await sleep(nextDelayMs);
    }
  }

  throw new Error(
    `Network interrupted while creating the project. Retried ${maxRetries} times. Please try again.`,
  );
};

export const createMiroFishProjectFromUrl = async ({
  prompt,
  url,
  clientSubmissionId = createMiroFishClientSubmissionId(),
  options = {},
}: {
  prompt: string;
  url: string;
  clientSubmissionId?: string;
  options?: MiroFishProjectCreateOptions;
}) => {
  const maxRetries = Math.max(
    0,
    options.maxRetries ?? PROJECT_CREATE_RETRY_DELAYS_MS.length,
  );
  let lastNetworkError: unknown;

  options.onProgress?.({
    progress: 18,
    label: "Downloading linked document...",
  });
  for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
    try {
      const response = await fetch("/api/mirofish/projects/import-url", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prompt,
          url,
          clientSubmissionId,
        }),
      });

      await throwIfNotOk(response);
      options.onProgress?.({ progress: 88, label: "Creating project..." });
      return (await response.json()) as { project: MiroFishProjectRecord };
    } catch (error) {
      if (!isNetworkFetchError(error) || attempt >= maxRetries) {
        if (lastNetworkError && isNetworkFetchError(error)) {
          throw new Error(
            `Network interrupted while importing the link. Retried ${maxRetries} times. Please try again.`,
          );
        }
        throw error;
      }

      lastNetworkError = error;
      const nextDelayMs =
        PROJECT_CREATE_RETRY_DELAYS_MS[
          Math.min(attempt, PROJECT_CREATE_RETRY_DELAYS_MS.length - 1)
        ];
      options.onRetry?.({
        retry: attempt + 1,
        maxRetries,
        nextDelayMs,
      });
      await sleep(nextDelayMs);
    }
  }

  throw new Error(
    `Network interrupted while importing the link. Retried ${maxRetries} times. Please try again.`,
  );
};

export const createMiroFishProject = async (
  formData: FormData,
  options: MiroFishProjectCreateOptions = {},
) => {
  const maxRetries = Math.max(
    0,
    options.maxRetries ?? PROJECT_CREATE_RETRY_DELAYS_MS.length,
  );
  let lastNetworkError: unknown;

  for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
    try {
      const response = await fetch("/api/mirofish/projects", {
        method: "POST",
        body: cloneFormData(formData),
      });

      await throwIfNotOk(response);
      return (await response.json()) as { project: MiroFishProjectRecord };
    } catch (error) {
      if (!isNetworkFetchError(error) || attempt >= maxRetries) {
        if (lastNetworkError && isNetworkFetchError(error)) {
          throw new Error(
            `Network interrupted while creating the project. Retried ${maxRetries} times. Please try again.`,
          );
        }
        throw error;
      }

      lastNetworkError = error;
      const nextDelayMs =
        PROJECT_CREATE_RETRY_DELAYS_MS[
          Math.min(attempt, PROJECT_CREATE_RETRY_DELAYS_MS.length - 1)
        ];
      options.onRetry?.({
        retry: attempt + 1,
        maxRetries,
        nextDelayMs,
      });
      await sleep(nextDelayMs);
    }
  }

  throw new Error(
    `Network interrupted while creating the project. Retried ${maxRetries} times. Please try again.`,
  );
};

const miroFishLocaleHeaders = (): Record<string, string> => {
  const lang =
    typeof document === "undefined" ? "" : document.documentElement.lang;
  return { "x-mirofish-locale": normalizeLocale(lang) ?? defaultLocale };
};

export const getMiroFishHistory = async () => {
  const response = await fetch("/api/mirofish/history", {
    cache: "no-store",
  });

  await throwIfNotOk(response);
  return (await response.json()) as { projects: MiroFishProjectSummary[] };
};

export const getMiroFishProject = async (projectId: string) => {
  const response = await fetch(`/api/mirofish/projects/${projectId}`, {
    cache: "no-store",
    // Polling read: must settle so the process-page poller can never freeze
    // on a hung request (a frozen poller also stops server-side provisioning).
    signal: AbortSignal.timeout(30_000),
  });

  await throwIfNotOk(response);
  return (await response.json()) as { project: MiroFishProjectRecord };
};

export const ensureMiroFishSimulation = async (projectId: string) => {
  const response = await fetch(
    `/api/mirofish/projects/${projectId}/simulation`,
    {
      method: "POST",
      cache: "no-store",
      signal: AbortSignal.timeout(60_000),
    },
  );

  await throwIfNotOk(response);
  return (await response.json()) as { project: MiroFishProjectRecord };
};

export const getMiroFishGraph = async (graphId: string, projectId?: string) => {
  const qs =
    projectId !== undefined && projectId !== ""
      ? `?projectId=${encodeURIComponent(projectId)}`
      : "";
  const response = await fetch(`/api/mirofish/graphs/${graphId}${qs}`, {
    cache: "no-store",
    signal: AbortSignal.timeout(30_000),
  });

  await throwIfNotOk(response);
  return (await response.json()) as { graph: MiroFishGraphPayload };
};

export const getMiroFishSimulation = async (simulationId: string) => {
  const response = await fetch(`/api/mirofish/simulations/${simulationId}`, {
    cache: "no-store",
  });

  await throwIfNotOk(response);
  return (await response.json()) as { simulation: MiroFishSimulationPayload };
};

export const startMiroFishSimulation = async (simulationId: string) => {
  const response = await fetch(
    `/api/mirofish/simulations/${simulationId}/start`,
    {
      method: "POST",
    },
  );

  await throwIfNotOk(response);
  return (await response.json()) as { simulation: MiroFishSimulationPayload };
};

const hydrateProjectFromReport = async (report: MiroFishReportResource) => {
  const [projectResponse, simulationResponse, chatResponse] = await Promise.all(
    [
      getMiroFishProject(report.projectId),
      getMiroFishSimulation(report.simulationId),
      fetch(`/api/mirofish/reports/${report.reportId}/chat`, {
        cache: "no-store",
        headers: miroFishLocaleHeaders(),
      }).then(async (response) => {
        await throwIfNotOk(response);
        return (await response.json()) as {
          messages: MiroFishInteractionMessage[];
        };
      }),
    ],
  );
  const project = projectResponse.project;
  return {
    ...project,
    graphId: report.graphId || project.graphId,
    simulationId: report.simulationId || project.simulationId,
    status:
      report.status === "completed"
        ? ("report_ready" as const)
        : report.status === "generating"
          ? ("report_generating" as const)
          : report.status === "failed"
            ? ("failed" as const)
            : project.status,
    report: reportResourceToPayload(report),
    simulation: {
      ...simulationResponse.simulation,
      analytics: report.aggregates,
    },
    chats: chatResponse.messages,
  };
};

export const getMiroFishReport = async (reportId: string) => {
  const response = await fetch(`/api/mirofish/reports/${reportId}`, {
    cache: "no-store",
    headers: miroFishLocaleHeaders(),
    // Polling read: settle within 30s so the report-page poller keeps its
    // backoff/stop logic running instead of hanging forever on one request.
    signal: AbortSignal.timeout(30_000),
  });

  await throwIfNotOk(response);
  const report = (await response.json()) as MiroFishReportResource;
  return { report, project: await hydrateProjectFromReport(report) };
};

export const requestMiroFishReport = async (
  reportId: string,
  options?: { forceRegenerate?: boolean },
) => {
  const response = await fetch(`/api/mirofish/reports/${reportId}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...miroFishLocaleHeaders(),
    },
    body: JSON.stringify({
      forceRegenerate: options?.forceRegenerate ?? false,
    }),
  });

  await throwIfNotOk(response);
  const report = (await response.json()) as MiroFishReportResource;
  return { report, project: await hydrateProjectFromReport(report) };
};

export const getMiroFishReportChat = async (
  reportId: string,
  mode?: "report-agent" | "world-character",
) => {
  const query = mode ? `?mode=${encodeURIComponent(mode)}` : "";
  const response = await fetch(
    `/api/mirofish/reports/${reportId}/chat${query}`,
    {
      cache: "no-store",
      headers: miroFishLocaleHeaders(),
    },
  );
  await throwIfNotOk(response);
  return (await response.json()) as {
    object: "list";
    reportId: string;
    messages: MiroFishProjectRecord["chats"];
  };
};

export const chatWithMiroFishReport = async ({
  reportId,
  message,
  mode,
  speaker,
}: {
  reportId: string;
  message: string;
  mode: "report-agent" | "world-character";
  speaker: string;
}) => {
  const response = await fetch(`/api/mirofish/reports/${reportId}/chat`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...miroFishLocaleHeaders(),
    },
    body: JSON.stringify({ message, mode, speaker }),
  });

  await throwIfNotOk(response);
  return (await response.json()) as {
    messages: MiroFishProjectRecord["chats"];
  };
};
